import axios from "axios";
import { tokenManager } from "utils/tokenManager";

const KPI_API_BASE = "https://usibras-web-backend.onrender.com/api";

/**
 * Fetch sector KPIs by sector and role.
 *
 * @param {number|string} sectorId - Sector ID
 * @param {string} role - "operator" | "supervisor"
 * @returns {Promise<{ data: Array<{ id, sector_id, sector_name, role, name, definition, created_at, updated_at }>, message: string }>}
 */
export async function getSectorKpis(sectorId, role) {
  const authHeader = tokenManager.getAuthHeader();
  const headers = {};
  if (authHeader) {
    headers.Authorization = authHeader;
  }
  const params = { sector_id: sectorId, role: role || "operator" };
  const response = await axios.get(`${KPI_API_BASE}/sector-kpis`, { headers, params });
  return response.data;
}

/**
 * Create a sector KPI.
 *
 * @param {Object} data
 * @param {number} data.sector_id - Sector ID
 * @param {string} data.sector_name - Sector name
 * @param {string} data.role - "operator" | "supervisor"
 * @param {string} data.name - KPI name
 * @param {string} [data.definition] - KPI definition/description
 * @returns {Promise<{ data: { id, sector_id, sector_name, role, name, definition, created_at, updated_at }, message: string }>}
 */
/**
 * Update a sector KPI.
 *
 * @param {number|string} kpiId - The KPI record ID
 * @param {Object} data
 * @param {number|string} data.sector_id - Sector ID
 * @param {string} data.sector_name - Sector name
 * @param {string} data.role - "operator" | "supervisor"
 * @param {string} data.name - KPI name
 * @param {string} [data.definition] - KPI definition/description
 * @returns {Promise<{ data: Object, message: string }>}
 */
export async function updateSectorKpi(kpiId, data) {
  const authHeader = tokenManager.getAuthHeader();
  const headers = { "Content-Type": "application/json" };
  if (authHeader) {
    headers.Authorization = authHeader;
  }
  // Build payload to match sample API: send name, definition and integer target only
  const rawTarget = data.target != null && String(data.target).trim() !== "" ? Number(data.target) : undefined;
  const targetValue = Number.isFinite(rawTarget) ? Math.round(rawTarget) : undefined;

  const payload = {};
  if (data.name != null) payload.name = data.name?.trim() || "";
  if (data.definition != null) payload.definition = data.definition?.trim() || "";
  if (targetValue !== undefined) payload.target = targetValue;

  const response = await axios.put(`${KPI_API_BASE}/sector-kpis/${kpiId}`, payload, { headers });
  return response.data;
}

/**
 * Delete a sector KPI.
 *
 * @param {number|string} kpiId - The KPI record ID
 * @returns {Promise<{ id: number, message: string }>}
 */
export async function deleteSectorKpi(kpiId) {
  const authHeader = tokenManager.getAuthHeader();
  const headers = {};
  if (authHeader) {
    headers.Authorization = authHeader;
  }
  const response = await axios.delete(`${KPI_API_BASE}/sector-kpis/${kpiId}`, { headers });
  return response.data;
}

export async function createSectorKpi(data) {
  const authHeader = tokenManager.getAuthHeader();
  const headers = {};
  if (authHeader) {
    headers.Authorization = authHeader;
  }
  // Coerce target to integer if provided and valid; omit when not a valid number
  const rawTarget = data.target != null && String(data.target).trim() !== "" ? Number(data.target) : undefined;
  const targetValue = Number.isFinite(rawTarget) ? Math.round(rawTarget) : undefined;
  const payload = {
    sector_id: data.sector_id,
    sector_name: data.sector_name || "",
    role: data.role || "operator",
    name: data.name?.trim() || "",
    definition: data.definition?.trim() || "",
  };
  if (targetValue !== undefined) payload.target = targetValue;

  const response = await axios.post(`${KPI_API_BASE}/sector-kpis`, payload, { headers });
  return response.data;
}
